import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TutorService } from '../tutor.service';
import { GlobalFunctions } from '../../../../core/global-functions';
import { CommonService } from '../../../../core/common.service';
import { CONSTANTS } from '../../../../core/constants';

@Component({
  selector: 'app-languages',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DropdownModule, RouterModule, ToastModule],
  templateUrl: './languages.component.html',
  styleUrl: './languages.component.scss'
})
export class LanguagesComponent implements OnInit {
  languagesList: any[] = [];
  languagesForm: FormGroup;
  constants: any = CONSTANTS;
  isBtnLoading: boolean = false;
  profileDetails: any;

  constructor(
    private _formBuilder: FormBuilder,
    private _router: Router,
    private messageService: MessageService,
    private _tutorServices: TutorService,
    private _globalFunctions: GlobalFunctions,
    private _commonService: CommonService
  ) {
    this.languagesForm = this._formBuilder.group({
      languages: this._formBuilder.array([], [Validators.required, Validators.minLength(1), Validators.maxLength(5)])
    });
  }

  ngOnInit(): void {
    this.getUserDetails();
    this.getLanguagesList();
  }

  getUserDetails() {
    this._commonService.getProfile().subscribe({
      next: (result: any) => {
        if (result && result.success && result.data) {
          this.profileDetails = result.data;
          this._commonService.setProfileData(result.data); // Update local storage
          this.prepareLanguagesFormObj(result.data);
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      }
    });
  }

  getLanguagesList() {
    this._commonService.getLanguages({ search: '', page: 1, limit: 30 }).subscribe((result: any) => {
      if (result && result?.success) {
        this.languagesList = result?.data?.languages;
      } else {
        this._globalFunctions.successErrorHandling(result?.message, this, true);
      }
    }, (error: any) => {
      this._globalFunctions.errorHanding(error, this, true);
    });
  }

  get languages(): FormArray {
    return this.languagesForm.get('languages') as FormArray;
  }

  createLanguagesListForm(tempObj: any = {}) {
    return this._formBuilder.group({
      language: [tempObj?.id || tempObj?._id || '', [Validators.required]],
      level: [tempObj?.TutorLanguage?.level || '', [Validators.required]],
    });
  }

  addLanguages() {
    if (this.languages.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Oops..!', detail: 'Please fill in all language fields before adding a new one.' });
      return;
    }
    if (this.languages.length >= 5) {
      this.messageService.add({ severity: 'error', summary: 'Oops..!', detail: 'You can add a maximum of 5 languages.' });
      return;
    }
    this.languages.push(this.createLanguagesListForm());
  }

  removeLanguages(removeAt: number) {
    if (this.languages.length > 1) {
      this.languages.removeAt(removeAt);
    } else {
      this.messageService.add({ severity: 'error', summary: 'Oops..!', detail: 'At least one language is required.' });
    }
  }

  getFilteredLanguages(index: number): any[] {
    if (!this.languagesList) return [];
    const selectedLanguages = this.languages.value
      .map((lang: any, i: number) => i !== index ? lang.language : null)
      .filter((lang: any) => lang !== null);

    return this.languagesList.filter((lang: any) => !selectedLanguages.includes(lang.id || lang._id));
  }

  goBack() {
    this._router.navigate(['/tutor-signup/' + this.constants.tutorSubjectsUrl]);
  }

  prepareLanguagesFormObj(tempObj: any = {}) {
    const languagesControls: any[] = [];
    if (tempObj?.languages && tempObj?.languages.length > 0) {
      tempObj.languages.forEach((element: any) => {
        languagesControls.push(this.createLanguagesListForm(element));
      });
    } else {
      languagesControls.push(this.createLanguagesListForm());
    }
    this.languagesForm.setControl('languages', this._formBuilder.array(languagesControls, [Validators.required, Validators.minLength(1), Validators.maxLength(5)]));
  }

  updateLanguages() {
    if (this.languagesForm.invalid) {
      this.languagesForm.markAllAsTouched();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please complete all fields correctly.' });
      return;
    }

    this.isBtnLoading = true;
    const payload = {
      languages: this.languagesForm.value.languages.map((l: any) => ({
        language_id: l.language,
        level: l.level
      }))
    };

    this._tutorServices.updateLanguages(payload).subscribe({
      next: (result: any) => {
        if (result && result?.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result.message });
          this._commonService.getProfile(false, true).subscribe((profileResult: any) => {
            if (profileResult && profileResult?.success) {
              this._commonService.setProfileData(profileResult?.data);
              this._router.navigate(['/tutor-signup/' + this.constants.tutorEducationUrl]);
            }
            this.isBtnLoading = false;
          });
        } else {
          this.isBtnLoading = false;
          this._globalFunctions.successErrorHandling(result, this, true);
        }
      },
      error: (error: any) => {
        this.isBtnLoading = false;
        this._globalFunctions.errorHanding(error, this, true);
      }
    });
  }
}
